import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { clearAuth, getAuthToken } from '@/stores/authStore';
import type { ApiErrorBody } from '@/types/auth';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/hkm-api';

const PUBLIC_PATH_PREFIXES = ['/public/'];

export const apiClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

function isPublicPath(url: string | undefined): boolean {
  if (!url) return false;
  return PUBLIC_PATH_PREFIXES.some((prefix) => url.includes(prefix));
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!isPublicPath(config.url)) {
    const token = getAuthToken();
    if (token) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorBody>) => {
    const status = error.response?.status;
    if (status === 401 && !isPublicPath(error.config?.url)) {
      clearAuth();
      onUnauthorized?.();
    }
    return Promise.reject(error);
  },
);

export function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof AxiosError) {
    const body = error.response?.data as ApiErrorBody | undefined;
    if (body?.message) return body.message;
    if (error.message) return error.message;
  }
  return fallback;
}
